// This is the entry point for the home component
import React, { FC, useCallback, useMemo, useContext } from 'react';

// Redux
import { useSelector, useDispatch } from 'react-redux';

// Styling
import injectSheet from 'react-jss';

// Constants

// Actions

// Components
import { FilterRadioHidden } from 'Components/FilterRadioHidden';
import CheckBox from 'Components/CheckBox';
import MultiSelect from 'Components/MultiSelect';
import { Accordion } from 'Components/Accordion';

// Utilities
import { areEqual } from 'Utils/areEqual';

import { ProductAnalyticsContext } from 'Context/ProductAnalytics';
import withErrorBoundary from 'HOC/ErrorBoundary';
import {
  setUSAValue,
  setInternationalValue,
} from './actions/geography.actions';
import {
  US_REGIONS,
  US_STATES_AND_TERRITORIES,
  COUNTRIES,
} from './constants/geography.constants';
import styleSheet from './styles';

interface Props {
  classes: any;
}

const Geography: FC<Props> = (props: Props) => {
  const { usa, international } = useSelector(
    (selectors: any) => selectors.filter.demographics.geography
  );
  const { usaQuery, internationalQuery } = useSelector(
    (selectors: any) => selectors.filter.query
  );
  const dispatch = useDispatch();

  const {
    filterTrackingHelpers: {
      Demographics: { Geography: GeograhphyTrackingHelper },
    },
    actions: { filterTrack },
  } = useContext(ProductAnalyticsContext);

  const { classes } = props;

  const usaCallback = (event: any) => {
    const { id, value } = event.target;
    const filterData = GeograhphyTrackingHelper.usa(value);
    // Combined Analytics Event
    filterTrack(filterData);

    setUSAValue(dispatch, usa, value, id);
  };

  const internationalCallback = (event: any) => {
    const { id, value } = event.target;
    const filterData = GeograhphyTrackingHelper.international(value);
    // Combined Analytics Event
    filterTrack(filterData);
    setInternationalValue(dispatch, international, value, id);
  };

  const onChange = (
    event: any,
    geography: 'usa' | 'international',
    control: 'checkbox' | 'dropdown'
  ) => {
    let newSelected = [
      ...((geography === 'usa' && usa) || international).value,
    ];
    if (
      ((geography === 'usa' && usa) || international).value.some(
        (option: any) => {
          return areEqual(
            event.target[(control === 'checkbox' && 'id') || 'value'],
            option
          );
        }
      )
    ) {
      newSelected = newSelected.filter((option) => {
        return !areEqual(
          event.target[(control === 'checkbox' && 'id') || 'value'],
          option
        );
      });
    } else {
      newSelected.push(
        event.target[(control === 'checkbox' && 'id') || 'value']
      );
    }

    const response = { target: { value: newSelected, id: 'value' } };

    if (geography === 'usa') {
      usaCallback(response);
    } else {
      internationalCallback(response);
    }
  };
  const onCheckBoxChangeUSA = (event: any) => {
    onChange(event, 'usa', 'checkbox');
  };

  const onDropdownChangeUSA = (event: any) => {
    onChange(event, 'usa', 'dropdown');
  };

  const onDropdownChangeInternational = (event: any) => {
    onChange(event, 'international', 'dropdown');
  };

  const mapUSStatesAndTerritories = () => {
    const usStatesAndTerritories = US_STATES_AND_TERRITORIES.map(
      (usStateOrTerritory) => {
        return {
          label: `${Object.keys(usStateOrTerritory)[0]} (${
            usStateOrTerritory[Object.keys(usStateOrTerritory)[0]]
          })`,
          value: usStateOrTerritory[Object.keys(usStateOrTerritory)[0]],
        };
      }
    );

    return usStatesAndTerritories;
  };

  const mapCountries = () => {
    const countries = COUNTRIES.map((country) => {
      return {
        label: `${Object.keys(country)[0]} (${
          country[Object.keys(country)[0]]
        })`,
        value: country[Object.keys(country)[0]],
      };
    });

    return countries;
  };

  const memoizedUSStatesAndTerritories = useMemo(
    () => mapUSStatesAndTerritories(),
    []
  );
  const memoizedCountries = useMemo(() => mapCountries(), []);

  return (
    <div className={classes.container}>
      <Accordion
        title="USA"
        id="usa"
        isOpen={usa.selection !== 'None'}
        accordionCallback={() => {
          if (usa.selection !== 'None') {
            usaCallback({
              target: {
                id: 'selectionUSA',
                value: 'None',
              },
            });
          } else {
            usaCallback({
              target: {
                id: 'selectionUSA',
                value: 'Any',
              },
            });
          }
        }}
        useTopDivider={false}
        useBottomDivider={false}
        accordionType="Checkbox"
        isActive={usaQuery !== ''}
        overrideStyles={{
          marginLeft: '22px',
        }}
        overrideStylesChildContainer={{
          marginLeft: '20px',
        }}
      >
        <div className={classes.container}>
          <FilterRadioHidden
            label="All USA"
            value="Any"
            selected={usa.selection === 'Any'}
            identifier="selectionUSA"
            onClick={useCallback(usaCallback, [usa])}
          />
          <FilterRadioHidden
            label="US Regions"
            value="US Regions"
            selected={usa.selection === 'US Regions'}
            identifier="selectionUSA"
            onClick={useCallback(usaCallback, [usa])}
          >
            {US_REGIONS.map((usRegion, index) => {
              return (
                <CheckBox
                  key={usRegion}
                  label={usRegion}
                  isChecked={usa.value.includes(usRegion)}
                  id={usRegion}
                  onClick={onCheckBoxChangeUSA}
                  overrideStyles={
                    (index === 0 && { margin: '8px 0 17px' }) ||
                    (index === US_REGIONS.length - 1 && {
                      margin: '0 0 8px',
                    }) || { margin: '0 0 17px' }
                  }
                />
              );
            })}
          </FilterRadioHidden>
          <FilterRadioHidden
            label="In-State"
            value="In-State"
            selected={usa.selection === 'In-State'}
            identifier="selectionUSA"
            onClick={useCallback(usaCallback, [usa])}
          />
          <FilterRadioHidden
            label="Out-of-State"
            value="Out-of-State"
            selected={usa.selection === 'Out-of-State'}
            identifier="selectionUSA"
            onClick={useCallback(usaCallback, [usa])}
          />
          <FilterRadioHidden
            label="Specific States/Territories"
            value="Specific States/Territories"
            selected={usa.selection === 'Specific States/Territories'}
            identifier="selectionUSA"
            onClick={useCallback(usaCallback, [usa])}
            overrideStyles={{
              marginLeft: '27px',
            }}
          >
            <MultiSelect
              selectedOptions={usa.value}
              items={memoizedUSStatesAndTerritories}
              onDropdownChange={useCallback(onDropdownChangeUSA, [usa])}
              selectionDescription={
                (usa.value.length > 1 && 'States/Territories') ||
                'State/Territory'
              }
              placeholder="Search State/Territory"
            />
          </FilterRadioHidden>
        </div>
      </Accordion>
      <Accordion
        title="International"
        id="international"
        isOpen={international.selection !== 'None'}
        accordionCallback={() => {
          if (international.selection !== 'None') {
            internationalCallback({
              target: {
                id: 'selectionInternational',
                value: 'None',
              },
            });
          } else {
            internationalCallback({
              target: {
                id: 'selectionInternational',
                value: 'Any',
              },
            });
          }
        }}
        useTopDivider={false}
        useBottomDivider={false}
        accordionType="Checkbox"
        isActive={internationalQuery !== ''}
        overrideStyles={{
          marginLeft: '22px',
        }}
        overrideStylesChildContainer={{
          marginLeft: '20px',
        }}
      >
        <div className={classes.container}>
          <FilterRadioHidden
            label="All International"
            value="Any"
            selected={international.selection === 'Any'}
            identifier="selectionInternational"
            onClick={useCallback(internationalCallback, [international])}
          />
          <FilterRadioHidden
            label="Specific Countries"
            value="Specific Countries"
            selected={international.selection === 'Specific Countries'}
            identifier="selectionInternational"
            onClick={useCallback(internationalCallback, [international])}
            overrideStyles={{
              marginLeft: '27px',
            }}
          >
            <MultiSelect
              selectedOptions={international.value}
              items={memoizedCountries}
              onDropdownChange={useCallback(onDropdownChangeInternational, [
                international,
              ])}
              selectionDescription={
                (international.value.length > 1 && 'Countries') || 'Country'
              }
              placeholder="Search Country"
            />
          </FilterRadioHidden>
        </div>
      </Accordion>
    </div>
  );
};

export default withErrorBoundary(injectSheet(styleSheet)(Geography));
